import { Component, inject, OnInit } from '@angular/core';
import { convivaCommonImports } from 'src/app/shared/ui/conviva-common';
import { angularCommonImports } from 'src/app/shared/ui/angular-common';
import { libsCommonImports } from 'src/app/shared/ui/libs-common';
import { NgxSpinnerService } from "ngx-spinner";
import { lastValueFrom } from "rxjs";
import { ConvivaErrorHandlingService } from 'src/app/shared/services/convivaErrorHandlingService';
import { ToastrService } from "ngx-toastr";
import { PlanoService } from "src/app/shared/services/plano.service";
import { Plano } from 'src/app/shared/models/plano/plano';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from "@angular/forms";
import { NgSelectConfig } from "@ng-select/ng-select";
import { ParametroPeriodo } from 'src/app/shared/models/parametro-periodo/parametro-periodo';
import { ParametroPeriodoService } from 'src/app/shared/services/parametro-periodo.service';

@Component({
  selector: 'app-parametros-periodo',
  templateUrl: './parametros-periodo.component.html',
  styleUrl: './parametros-periodo.component.scss',
  standalone: true,
  imports: [angularCommonImports, convivaCommonImports, libsCommonImports],
})

export class ParametrosPeriodoComponent implements OnInit {
  //Injeções
  private spinnerService = inject(NgxSpinnerService);
  private ConvivaErrors = inject(ConvivaErrorHandlingService);
  private toastr = inject(ToastrService);
  private planoService = inject(PlanoService);
  private parametroPeriodoService = inject(ParametroPeriodoService);
  private config = inject(NgSelectConfig);

  public formParametroPeriodo!: FormGroup;
  public submitted = false;
  public planos: Plano[] = [];
  public parametroPeriodo: ParametroPeriodo;
  public parametrosPeriodo: ParametroPeriodo[] = [];
  public editando = false;
  public parametroPeriodoEditando: ParametroPeriodo | null = null;
  public showModalExcluir = false;
  public parametroParaExcluir: ParametroPeriodo | null = null;

  ngOnInit(): void {
    this.inicializar();
  }

  private async inicializar(): Promise<void> {
    this.formParametroPeriodo = new FormGroup({
      codigoPlano: new FormControl(null, [Validators.required]),
      dataInicio: new FormControl(null, [Validators.required, this.dataValidaValidator]),
      dataFim: new FormControl(null, [Validators.required, this.dataValidaValidator])
    }, { validators: this.dataRangeValidator });

    await this.getPlanos();
    await this.getParametroPeriodo();
  }

  //Grid
  public colunasGrid = [
    { title: 'Plano', field: 'nmPlano', hozAlign: "center", headerHozAlign: "center", headerFilter: "input" },
    { title: 'Data Inicio', field: 'dtInicio', hozAlign: "center", headerHozAlign: "center", headerFilter: "input", formatter: (cell: any) => { const data = cell.getValue(); if (!data) return '-'; return new Date(data).toLocaleDateString('pt-BR'); } },
    { title: 'Data Fim', field: 'dtFim', hozAlign: "center", headerHozAlign: "center", headerFilter: "input", formatter: (cell: any) => { const data = cell.getValue(); if (!data) return '-'; return new Date(data).toLocaleDateString('pt-BR'); } },
    { title: 'Status', field: 'flAtivo', width: 200, hozAlign: "center", headerHozAlign: "center", formatter: (cell: any) => cell.getValue() ? 'Ativo' : 'Inativo', headerFilter: "input" },
    {title: 'Ações',
      hozAlign: "center",
      headerHozAlign: "center",
      width: 120,
      formatter: () => `
        <div style='display:flex;justify-content:center;gap:0.5rem;'>
          <i class='fa fa-pencil' style='cursor:pointer;color:#6c757d' title='Editar'></i>
          <i class='fa fa-trash' style='cursor:pointer;color:#6c757d' title='Excluir'></i>
        </div>`,
      cellClick: (e: any, cell: any) => {
        const icon = (e.target as HTMLElement);
        const rowData = cell.getRow().getData();
        if (icon.classList.contains('fa-pencil')) {
          this.onEditar(rowData);
        } else if (icon.classList.contains('fa-trash')) {
          this.abrirModalExcluir(rowData);
        }
      }
    }
];

  // Validador para verificar se a data é válida
  private dataValidaValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const data = new Date(control.value);
    return !isNaN(data.getTime()) ? null : { dataInvalida: true };
  }

  // Validador para verificar se data fim é maior que data início
  private dataRangeValidator(group: AbstractControl): ValidationErrors | null {
    const dataInicio = group.get('dataInicio')?.value;
    const dataFim = group.get('dataFim')?.value;

    if (!dataInicio || !dataFim) return null;

    const inicio = new Date(dataInicio);
    const fim = new Date(dataFim);

    if (inicio > fim) {
      group.get('dataFim')?.setErrors({ dataFimAnteriorInicio: true });
      return { dataFimAnteriorInicio: true };
    }

    // Remove o erro específico se existir
    const currentErrors = group.get('dataFim')?.errors;
    if (currentErrors?.['dataFimAnteriorInicio']) {
      delete currentErrors['dataFimAnteriorInicio'];
      group.get('dataFim')?.setErrors(Object.keys(currentErrors).length > 0 ? currentErrors : null);
    }

    return null;
  }

  public async onSalvar() {
    this.submitted = true;
    this.markAllControlsAsTouched();

    if (!this.formParametroPeriodo.valid) {
      this.toastr.warning('Por favor, corrija os erros no formulário antes de salvar.');
      this.scrollToFirstInvalidControl();
      return;
    }

    const codigoPlano = this.formParametroPeriodo.get('codigoPlano')?.value;
    if (!codigoPlano) {
      this.toastr.error('Plano inválido. Selecione um plano.');
      return;
    }

    this.spinnerService.show();
    try {
      const parametros = this.SetParametroPeriodo();
      const response = await lastValueFrom(this.parametroPeriodoService.salvarParametroPeriodo(parametros));

      if (!response.isSucess) {
        if (response.message?.includes('Sequence contains no elements')) {
          this.toastr.success('Parâmetro de período inserido com sucesso!');
          await this.getParametroPeriodo();
        } else {
          this.toastr.error(response.message);
        }
        return;
      }
      this.toastr.success(response.data.mensagem);
      await this.getParametroPeriodo();

    } catch (err: any) {
      const errorMessage = err?.error?.message || err?.message || '';

      if (errorMessage.includes('Sequence contains no elements')) {
        this.toastr.success('Parâmetro de período inserido com sucesso!');
        await this.getParametroPeriodo();
      } else {
        this.ConvivaErrors.handleResultError(err);
      }

    } finally {
      this.submitted = false;
      this.editando = false;
      this.parametroPeriodoEditando = null;
      this.formParametroPeriodo.reset();
      this.spinnerService.hide();
    }
  }



  public onEditar(item: ParametroPeriodo): void {
    this.editando = true;
    this.parametroPeriodoEditando = item;

    this.formParametroPeriodo.patchValue({
      codigoPlano: item.codigoPlano,
      dataInicio: item.dtInicio ? new Date(item.dtInicio).toISOString().substring(0, 10) : null,
      dataFim: item.dtFim ? new Date(item.dtFim).toISOString().substring(0, 10) : null
    });
  }

  public async onExcluir(item: ParametroPeriodo): Promise<void> {
    this.spinnerService.show();
    try {
      const response = await lastValueFrom(this.parametroPeriodoService.excluirParametroPeriodo(item.codigoParametroPeriodoPlano));

      if (!response.isSucess) {
        this.toastr.error(response.message);
        return;
      }

      this.toastr.success('Parâmetro de período excluído com sucesso!');
      this.getParametroPeriodo();
    } catch (err) {
      this.ConvivaErrors.handleResultError(err);
    } finally {
      this.spinnerService.hide();
    }
  }

  public onLimpar(): void {
    this.submitted = false;
    this.editando = false;
    this.parametroPeriodoEditando = null;
    this.formParametroPeriodo.reset();
    this.removeValidationStyles();
  }

  public onCancelar(): void {
    this.editando = false;
    this.parametroPeriodoEditando = null;
    this.formParametroPeriodo.reset();
  }

  public abrirModalExcluir(item: ParametroPeriodo): void {
    this.parametroParaExcluir = item;
    this.showModalExcluir = true;
  }

  public fecharModalExcluir(): void {
    this.parametroParaExcluir = null;
    this.showModalExcluir = false;
  }

  public confirmarExclusao(): void {
    if (this.parametroParaExcluir) {
      this.onExcluir(this.parametroParaExcluir);
    }
    this.fecharModalExcluir();
  }


  // Marca todos os controles como touched para trigger das validações
  private markAllControlsAsTouched(): void {
    Object.keys(this.formParametroPeriodo.controls).forEach(key => {
      const control = this.formParametroPeriodo.get(key);
      control?.markAsTouched();
    });
  }

  // Método auxiliar para rolar até o primeiro campo inválido
  private scrollToFirstInvalidControl(): void {
    setTimeout(() => {
      const firstInvalidControl = document.querySelector('.is-invalid');
      if (firstInvalidControl) {
        firstInvalidControl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  // Remove estilos de validação do DOM
  private removeValidationStyles(): void {
    const invalidElements = document.querySelectorAll('.is-invalid');
    invalidElements.forEach(element => {
      element.classList.remove('is-invalid');
    });
  }

  public async getPlanos() {
    this.spinnerService.show();
    try {
      const response = await lastValueFrom(this.planoService.getAllPlanos());

      if (!response.isSucess) {
        this.toastr.error(response.message);
        return;
      }

      this.planos = response.data;
    } catch (err) {
      this.ConvivaErrors.handleResultError(err);
    } finally {
      this.spinnerService.hide();
    }
  }

  public async getParametroPeriodo() {
    this.spinnerService.show();
    try {
      const response = await lastValueFrom(this.parametroPeriodoService.getAllParametroPeriodo());

      if (!response.isSucess) {
        this.toastr.error(response.message);
        return;
      }

      this.parametrosPeriodo = response.data;
    } catch (err) {
      this.ConvivaErrors.handleResultError(err);
    } finally {
      this.spinnerService.hide();
    }
  }

  private SetParametroPeriodo(): ParametroPeriodo {
    const codigoPlano = this.formParametroPeriodo.get('codigoPlano')?.value;
    const planoSelecionado = this.planos.find(p => p.codigoPlano === codigoPlano);

    if (!planoSelecionado) {
      this.toastr.error('Plano inválido. Selecione um plano.');
      throw new Error('Plano não encontrado');
    }

    const parametroPeriodo: ParametroPeriodo = {
      codigoParametroPeriodoPlano: this.parametroPeriodoEditando?.codigoParametroPeriodoPlano ?? 0,
      codigoPlano: codigoPlano,
      dtInicio: this.formParametroPeriodo.get('dataInicio')?.value,
      dtFim: this.formParametroPeriodo.get('dataFim')?.value,
      flAtivo: true
    };

    return parametroPeriodo;
  }


  // Getters para facilitar o acesso aos controles no template
  get codigoPlano() { return this.formParametroPeriodo.get('codigoPlano'); }
  get dataInicio() { return this.formParametroPeriodo.get('dataInicio'); }
  get dataFim() { return this.formParametroPeriodo.get('dataFim'); }
}
